import { useEffect, useState, type ReactNode } from 'react';
import type { InspectionTemplateBody } from '@ayumia/contracts';
import { text } from '../../app/i18n';
import { Icon } from '../../design-system/icons';
import { civilShown, initials } from '../FormLayoutParts';
import { useFormMedia } from './FormMedia';
import type { HeaderSettings, MediaRef } from './presentation';

/**
 * Bandeau réglable du formulaire (inspection-template-body 1.10.0, `layout.header` ; ADR-146 addenda (14)
 * et (16)) : « Sortir » (✕) toujours à gauche, le visuel et le titre du formulaire sous un surtitre, la date
 * de la fiche (et l'heure), l'opérateur (photo ou initiales, environnement), le raccourci « Valider » dans
 * l'apparence déclarée. Une seule action principale visible : la gate de publication le vérifie
 * (`LAYOUT_PRIMARY_ACTION_MULTIPLE`). Présentation seulement.
 */

type Visual = { image?: MediaRef; icon?: string; background?: string } | undefined;

/** Heure courante « HH:MM », au fuseau donné (celui du site), sinon rien : jamais l'heure UTC par défaut. */
function clock(now: Date, timeZone: string | undefined): string | undefined {
  if (timeZone === undefined) return undefined;
  try {
    return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone }).format(now);
  } catch {
    return undefined;
  }
}

function useMinute(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

export function FormVisual({ visual, size = 34 }: { visual: Visual; size?: number }) {
  const media = useFormMedia();
  if (visual === undefined) return null;
  const url = media.resolve(visual.image);
  const background = visual.background ?? 'accent';
  return (
    <span className={`ay-fv2-visual ay-fv2-visual--${background}`} style={{ width: size, height: size }} aria-hidden="true">
      {url !== undefined ? <img src={url} alt="" /> : <Icon name={visual.icon ?? 'clipboard'} size={Math.round(size * 0.6)} />}
    </span>
  );
}

export function FormHeader({
  template,
  settings,
  sheetDate,
  timeZone,
  operator,
  disabled,
  onExit,
  showSubmit,
  children,
}: {
  template: InspectionTemplateBody;
  settings: HeaderSettings;
  sheetDate: string | undefined;
  timeZone: string | undefined;
  operator: string | undefined;
  disabled: boolean;
  onExit?: (() => void) | undefined;
  showSubmit: boolean;
  children?: ReactNode;
}) {
  const media = useFormMedia();
  const now = useMinute();
  const time = settings.date === 'datetime' ? clock(now, timeZone) : undefined;
  const visual = (template as { visual?: Visual }).visual;
  const title = text(template.label);
  const who = operator === undefined || operator === '' ? '—' : operator;
  const { submit } = settings;
  return (
    <div className="ay-fv2-header" role="region" aria-label="Bandeau de la fiche" data-testid="form-banner">
      <div className="ay-fv2-header__start">
        {onExit !== undefined && (
          <button type="button" className="ay-fv2-iconbtn" aria-label="Sortir" title="Sortir" onClick={onExit}>
            <Icon name="close" size={20} />
          </button>
        )}
        <span className="ay-fv2-pill ay-fv2-header__title">
          {settings.showVisual && <FormVisual visual={visual} />}
          <span className="ay-fv2-header__names">
            {settings.overline !== '' && <span className="ay-fv2-overline">{settings.overline}</span>}
            <strong className="ay-fv2-header__label">{title}</strong>
          </span>
        </span>
      </div>
      {children !== undefined && <div className="ay-fv2-header__blocks">{children}</div>}
      <div className="ay-fv2-header__center">
        {settings.date !== 'none' && (
          <span className="ay-fv2-pill ay-fv2-header__date">
            <Icon name="calendar" size={16} />
            <strong data-testid="banner-date">{civilShown(sheetDate)}</strong>
            {time !== undefined && (
              <>
                <span className="ay-fv2-header__dot" aria-hidden="true">
                  ·
                </span>
                <Icon name="clock" size={16} />
                <strong data-testid="banner-time">{time}</strong>
              </>
            )}
          </span>
        )}
      </div>
      <div className="ay-fv2-header__end">
        {settings.operator !== 'none' && (
          <span className="ay-fv2-pill ay-fv2-header__operator">
            {settings.operator === 'photo_and_name' && (
              <span className="ay-fv2-avatar" aria-hidden="true">
                {media.operatorPhoto !== undefined ? <img src={media.operatorPhoto} alt="" /> : <span>{initials(who) || '—'}</span>}
                <span className="ay-fv2-avatar__presence" />
              </span>
            )}
            <span className="ay-fv2-header__names">
              <strong data-testid="banner-operator">{who}</strong>
              {settings.environmentBadge && media.environment !== undefined && <span className="ay-fv2-overline ay-fv2-header__env">{media.environment}</span>}
            </span>
          </span>
        )}
        {showSubmit && (
          <button type="submit" className={`ay-fv2-submit ay-fv2-submit--${submit.appearance}`} disabled={disabled} aria-busy={disabled} data-testid="banner-validate">
            {submit.icon !== undefined && <Icon name={submit.icon} size={18} />}
            <span>{submit.label}</span>
          </button>
        )}
      </div>
    </div>
  );
}
